import { AnimatePresence, motion } from 'motion/react';
import type { ReactNode } from 'react';
import { create } from 'zustand';

import { cn } from '@/shared/lib/cn';
import { createId } from '@/shared/lib/id';

export type ToastTone = 'neutral' | 'success' | 'danger' | 'info';

interface ToastItem {
  id: string;
  message: string;
  tone: ToastTone;
  /** Кнопка действия — обычно «Отменить» после удаления. */
  action?: { label: string; onClick: () => void };
}

interface ToastStore {
  items: ToastItem[];
  push: (toast: Omit<ToastItem, 'id'>, durationMs?: number) => void;
  dismiss: (id: string) => void;
}

const useToastStore = create<ToastStore>((set, get) => ({
  items: [],
  push: (toast, durationMs = 3200) => {
    const id = createId();
    set((state) => ({ items: [...state.items, { ...toast, id }] }));
    // С действием даём больше времени: пользователю нужно успеть нажать.
    const timeout = toast.action ? Math.max(durationMs, 5000) : durationMs;
    setTimeout(() => get().dismiss(id), timeout);
  },
  dismiss: (id) => set((state) => ({ items: state.items.filter((item) => item.id !== id) })),
}));

/**
 * Короткое уведомление о результате действия.
 *
 * ```ts
 * toast.success('Задача выполнена');
 * toast.undo('Покупка удалена', () => restoreItem(id));
 * ```
 */
/* eslint-disable react-refresh/only-export-components --
   API уведомлений живёт рядом с компонентом намеренно: разнесение по файлам
   ради горячей перезагрузки усложнило бы импорт без выигрыша для разработки. */
export const toast = {
  show: (message: string, tone: ToastTone = 'neutral') =>
    useToastStore.getState().push({ message, tone }),
  success: (message: string) => useToastStore.getState().push({ message, tone: 'success' }),
  error: (message: string) => useToastStore.getState().push({ message, tone: 'danger' }),
  info: (message: string) => useToastStore.getState().push({ message, tone: 'info' }),
  /** Уведомление с возможностью отменить действие. */
  undo: (message: string, onUndo: () => void) =>
    useToastStore.getState().push({
      message,
      tone: 'neutral',
      action: { label: 'Отменить', onClick: onUndo },
    }),
};

const TONE_STYLES: Record<ToastTone, string> = {
  neutral: 'bg-surface-3 text-content',
  success: 'bg-success text-white',
  danger: 'bg-danger text-white',
  info: 'bg-info text-white',
};

/** Контейнер уведомлений. Монтируется один раз в корне приложения. */
export function ToastViewport(): ReactNode {
  const items = useToastStore((state) => state.items);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <div
      className="pointer-events-none fixed inset-x-content z-[60] flex flex-col items-center gap-2 px-4"
      // Над нижней навигацией, чтобы уведомление не перекрывало табы.
      style={{ bottom: 'calc(var(--nav-height) + var(--safe-bottom) + 1rem)' }}
      role="status"
      aria-live="polite"
    >
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <motion.div
            key={item.id}
            layout
            initial={{ opacity: 0, y: 16, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ type: 'spring', damping: 26, stiffness: 360 }}
            className={cn(
              'pointer-events-auto flex w-full max-w-sm items-center gap-3',
              'rounded-full px-4 py-3 text-[14px] font-medium shadow-lg',
              TONE_STYLES[item.tone],
            )}
          >
            <span className="min-w-0 flex-1">{item.message}</span>
            {item.action && (
              <button
                type="button"
                onClick={() => {
                  item.action?.onClick();
                  dismiss(item.id);
                }}
                className="shrink-0 rounded-full px-2 py-0.5 font-semibold underline-offset-2 hover:underline"
              >
                {item.action.label}
              </button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
